import { clientApi, setClientId } from '@/lib/api-client'
import { getSignupPhone } from '@/lib/signup-session'

/** Fixed UUID used only when MSW mocks are enabled. */
export const MOCK_CLIENT_ID = '00000000-0000-4000-8000-000000000001'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isValidClientId(id: string | null | undefined): id is string {
  return Boolean(id && UUID_RE.test(id))
}

export function useMocks(): boolean {
  return import.meta.env.VITE_USE_MOCKS === 'true'
}

export function defaultClientIdFallback(): string {
  return MOCK_CLIENT_ID
}

export interface ClientProfileUser {
  email: string
  name?: string
}

/** Resolve client ID from the server draft; ignores stale localStorage values. */
export async function resolveClientId(): Promise<string | null> {
  if (useMocks()) {
    setClientId(MOCK_CLIENT_ID)
    return MOCK_CLIENT_ID
  }

  try {
    const draft = await clientApi.getDraft()
    if (isValidClientId(draft.client_id)) {
      setClientId(draft.client_id)
      return draft.client_id
    }
  } catch {
    // draft may not exist yet for new users
  }

  setClientId(null)
  return null
}

/**
 * Ensure a client profile exists for the authenticated user.
 * Creates a DRAFT profile via wizard draft when none exists yet.
 */
export async function ensureClientProfile(
  authUser?: ClientProfileUser | null,
): Promise<string | null> {
  const existing = await resolveClientId()
  if (existing) return existing

  if (useMocks()) {
    setClientId(MOCK_CLIENT_ID)
    return MOCK_CLIENT_ID
  }

  if (!authUser?.email) return null

  try {
    const phone = getSignupPhone()
    const response = await clientApi.patchDraft({
      personal_data: {
        email: authUser.email,
        ...(authUser.name?.trim() &&
        authUser.name.trim().toLowerCase() !== authUser.email.trim().toLowerCase()
          ? { full_name: authUser.name.trim() }
          : {}),
        ...(phone ? { mobile_phone: phone } : {}),
      },
    })
    if (isValidClientId(response.client_id)) {
      setClientId(response.client_id)
      return response.client_id
    }
  } catch {
    // bootstrap failed
  }

  return null
}
